import { Box, Button, Divider, Modal, Stack, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { useShipperShipmentPageContext, useWriterComponentState } from "hooks";
import { MODAL_STYLE } from "layouts/constants";
import { getFullName } from "utils";

import { ClientAppEvent } from "../../../../constants";

import type { CarrierBlockedSource } from "../../../../constants";

interface Props {
  open: boolean;
  onClose: VoidFunction;
  carrierId: number;
  companyName: string;
  firstName?: string;
  lastName?: string;
  // Which surface the block was started from, so blocks are attributable per
  // entry point rather than landing in one undifferentiated count.
  source: CarrierBlockedSource;
  // Lets a caller outside the shipment page react to a block it cannot refetch.
  onCarrierBlocked?: VoidFunction;
}

export function BlockCarrierModal(props: Props) {
  const {
    open,
    onClose,
    carrierId,
    companyName,
    firstName,
    lastName,
    source,
    onCarrierBlocked: onBlocked,
  } = props;
  const posthog = usePostHog();
  const { onRefresh } = useShipperShipmentPageContext();
  const {
    write: blockCarrier,
    error,
    loading,
  } = useWriterComponentState({
    errorMessage:
      "Failed to block carrier. Please contact us if the problem persists.",
    writeConfig: {
      method: "post",
      url: "/user/block-carrier",
      body: {
        carrier_id: carrierId,
      },
    },
  });

  const onCarrierBlocked = () => {
    posthog?.capture(ClientAppEvent.CARRIER_BLOCKED, { block_source: source });
    onClose();
    if (onBlocked) {
      onBlocked();
    }
    if (onRefresh) {
      onRefresh();
    }
  };

  const carrierName = getFullName(firstName, lastName);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={MODAL_STYLE}>
        <Typography variant="h6" component="h2" textAlign="center">
          Block Carrier
        </Typography>
        <Divider sx={{ my: "1rem" }} />
        <Typography>
          {
            "Blocking a carrier will prevent them from accessing and quoting your loads. Are you sure you want to block "
          }
          <Typography component="span" fontWeight="bold">
            {carrierName}
          </Typography>
          {" from "}
          <Typography component="span" fontWeight="bold">
            {companyName}
          </Typography>
          ?
        </Typography>
        <ErrorTypography error={error} />
        <LoadingWrappedNode loading={loading}>
          <Stack
            direction="row"
            justifyContent="center"
            spacing={2}
            sx={{ mt: "2rem" }}
          >
            <Button variant="contained" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => blockCarrier(onCarrierBlocked)}
              variant="outlined"
              color="error"
            >
              Block Carrier
            </Button>
          </Stack>
        </LoadingWrappedNode>
      </Box>
    </Modal>
  );
}
